import { trpc } from "@/lib/trpc";
import { formatBJ } from "@/lib/timeUtils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Bell, CheckCheck, ArrowUpCircle, ArrowDownCircle, AlertTriangle, TrendingUp, TrendingDown, Loader2, RefreshCw, Rss, Brain, Settings2 } from "lucide-react";
import NotificationSettingsPanel from "@/components/NotificationSettingsPanel";
import { toast } from "sonner";
import { useState } from "react";

export default function Notifications() {
  const [showAll, setShowAll] = useState(false);
  const { data: notifications, isLoading, refetch } = trpc.notifications.list.useQuery(
    showAll ? { limit: 100 } : { unreadOnly: true, limit: 50 }
  );
  const markRead = trpc.notifications.markRead.useMutation({
    onSuccess: () => refetch(),
  });
  const markAllRead = trpc.notifications.markAllRead.useMutation({
    onSuccess: () => {
      toast.success("已全部标记为已读");
      refetch();
    },
  });

  // LLM 使用统计
  const { data: llmStats } = trpc.llmUsage.stats.useQuery({ days: 7 });
  const { data: rateLimit } = trpc.llmUsage.rateLimit.useQuery();

  // 新闻获取状态
  const { data: schedulerStatus } = trpc.scheduler.status.useQuery();
  const triggerNewsFetch = trpc.newsCache.triggerFetch.useMutation({
    onSuccess: (result) => {
      toast.success(`新闻获取完成：新增 ${result.lastCount} 条`);
    },
    onError: () => toast.error("新闻获取失败"),
  });

  const getNotifIcon = (type: string) => {
    switch (type) {
      case "trade_buy": return <ArrowUpCircle className="h-4 w-4 text-red-400" />;
      case "trade_sell": return <ArrowDownCircle className="h-4 w-4 text-green-400" />;
      case "stop_loss": return <AlertTriangle className="h-4 w-4 text-amber-400" />;
      default: return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getNotifBadge = (type: string) => {
    switch (type) {
      case "trade_buy": return <Badge variant="outline" className="text-red-400 border-red-400/30 text-[10px]">买入</Badge>;
      case "trade_sell": return <Badge variant="outline" className="text-green-400 border-green-400/30 text-[10px]">卖出</Badge>;
      case "stop_loss": return <Badge variant="outline" className="text-amber-400 border-amber-400/30 text-[10px]">止损</Badge>;
      default: return <Badge variant="outline" className="text-[10px]">系统</Badge>;
    }
  };

  const unreadCount = notifications?.unreadCount ?? 0;
  const notifItems = notifications?.items ?? [];

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            通知中心
          </h1>
          <p className="text-sm text-muted-foreground mt-1">交易信号推送、LLM 使用统计、新闻获取状态</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAll(!showAll)}>
            {showAll ? "仅未读" : "显示全部"}
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              全部已读
            </Button>
          )}
        </div>
      </div>

      {/* 顶部统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* LLM 调用统计 */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium flex items-center gap-2">
              <Brain className="h-3.5 w-3.5 text-violet-400" />
              LLM 调用统计（7天）
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">总调用次数</span>
                <span className="font-mono font-medium">{llmStats?.totalCalls ?? 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">总 Token</span>
                <span className="font-mono font-medium">{(llmStats?.totalTokens ?? 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">估算费用</span>
                <span className="font-mono font-medium">¥{(llmStats?.totalCost ?? 0).toFixed(2)}</span>
              </div>
              {rateLimit?.enabled && (
                <>
                  <Separator className="bg-border/30" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">今日已用/上限</span>
                    <span className={`font-mono font-medium ${!rateLimit.allowed ? "text-red-400" : ""}`}>
                      {rateLimit.current}/{rateLimit.limit}
                    </span>
                  </div>
                  {!rateLimit.allowed && (
                    <p className="text-[10px] text-red-400">已达今日调用上限，AI 分析将暂停</p>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 新闻获取状态 */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium flex items-center gap-2">
              <Rss className="h-3.5 w-3.5 text-orange-400" />
              新闻定时获取
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">状态</span>
                <Badge variant="outline" className={`text-[10px] ${schedulerStatus?.newsFetch?.enabled ? "text-green-400 border-green-400/30" : "text-muted-foreground"}`}>
                  {schedulerStatus?.newsFetch?.enabled ? "已启用" : "未启用"}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">上次获取</span>
                <span className="font-mono text-xs">
                  {schedulerStatus?.newsFetch?.lastRunTime
                    ? formatBJ(schedulerStatus.newsFetch.lastRunTime, "datetime")
                    : "未执行"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">上次新增</span>
                <span className="font-mono font-medium">{schedulerStatus?.newsFetch?.lastCount ?? 0} 条</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">执行时间</span>
                <span className="font-mono text-xs">
                  {[schedulerStatus?.newsFetch?.scheduleTime1, schedulerStatus?.newsFetch?.scheduleTime2, schedulerStatus?.newsFetch?.scheduleTime3, schedulerStatus?.newsFetch?.scheduleTime4]
                    .filter((t): t is number => t !== undefined && t >= 0 && t <= 23)
                    .map(t => `${String(t).padStart(2, "0")}:30`)
                    .join(", ") || "未配置"}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-1"
                onClick={() => triggerNewsFetch.mutate()}
                disabled={triggerNewsFetch.isPending}
              >
                {triggerNewsFetch.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                立即获取
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 通知统计 */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium flex items-center gap-2">
              <Bell className="h-3.5 w-3.5 text-blue-400" />
              交易信号通知
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">未读通知</span>
                <span className={`font-mono font-medium ${unreadCount > 0 ? "text-amber-400" : ""}`}>{unreadCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">总通知数</span>
                <span className="font-mono font-medium">{notifItems.length}</span>
              </div>
              <div className="flex gap-2 mt-2">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ArrowUpCircle className="h-3 w-3 text-red-400" />
                  买入
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ArrowDownCircle className="h-3 w-3 text-green-400" />
                  卖出
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <AlertTriangle className="h-3 w-3 text-amber-400" />
                  止损
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LLM 调用来源分布 */}
      {llmStats?.bySource && Object.keys(llmStats.bySource).length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium">LLM 调用来源分布（7天）</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(llmStats.bySource).map(([source, stats]: [string, any]) => (
                <div key={source} className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <div className="text-xs text-muted-foreground mb-1">
                    {source === "event_analysis" ? "事件分析" :
                     source === "screening" ? "T+0 选股" :
                     source === "stock_analysis" ? "个股分析" :
                     source === "market_decision" ? "市场决策" :
                     source === "opportunity" ? "短期掘金" :
                     source === "long_term" ? "长期机会" : source}
                  </div>
                  <div className="text-lg font-mono font-bold">{stats.count}</div>
                  <div className="text-[10px] text-muted-foreground">{(stats.tokens || 0).toLocaleString()} tokens</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 通知设置面板 */}
      <NotificationSettingsPanel />

      {/* 通知列表 */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            {showAll ? "全部通知" : "未读通知"}
            {unreadCount > 0 && <Badge className="ml-2 text-[10px]" variant="destructive">{unreadCount}</Badge>}
          </CardTitle>
          <CardDescription className="text-xs">交易执行时自动推送买入/卖出/止损信号</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : notifItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{showAll ? "暂无通知" : "没有未读通知"}</p>
              <p className="text-xs mt-1">交易引擎执行买卖操作时将自动推送通知</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifItems.map((notif: any) => (
                <div
                  key={notif.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    notif.isRead ? "bg-muted/10 border-border/20" : "bg-primary/5 border-primary/20"
                  }`}
                  onClick={() => {
                    if (!notif.isRead) markRead.mutate({ notificationId: notif.id });
                  }}
                >
                  <div className="mt-0.5">{getNotifIcon(notif.notificationType)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium truncate">{notif.title}</span>
                      {getNotifBadge(notif.notificationType)}
                      {!notif.isRead && <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground">{notif.content}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                      <span>{notif.stockCode}</span>
                      {notif.tradePrice && <span>¥{Number(notif.tradePrice).toFixed(2)}</span>}
                      {notif.tradeQuantity && <span>{notif.tradeQuantity}股</span>}
                      {notif.tradePnl != null && (
                        <span className={Number(notif.tradePnl) >= 0 ? "text-red-400" : "text-green-400"}>
                          {Number(notif.tradePnl) >= 0 ? "+" : ""}{Number(notif.tradePnl).toFixed(2)}
                        </span>
                      )}
                      <span>{formatBJ(notif.createdAt, "datetime")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
